<script setup lang="ts">
import { computed, ref } from "vue";
import {
  displaySectionTitle,
  type Hit,
  type RetrievalEvent,
} from "../types/chat";
import GroupPill from "./GroupPill.vue";

const props = defineProps<{
  retrieval: RetrievalEvent;
  /** Stable id of the parent turn — used as a namespace for anchor IDs so
   * `[N]` clicks from MessageBubble scroll to *this* bubble's source row,
   * not a row in some earlier turn of the same conversation. */
  turnId?: string;
}>();

/** Groups worth showing the reader. The caller's own principal
 * (`user:<oid>`) is part of the ACL filter — personal KBs and private
 * uploads are granted to it — but as a pill it is a 36-character opaque
 * id that tells the person nothing they don't already know about
 * themselves. The filter still uses it; only the chip is dropped.
 * `filter used` below still shows the full expression. */
const displayGroups = computed(() =>
  (props.retrieval.groups ?? []).filter((g) => !g.startsWith("user:")),
);

/** Which hit's preview is currently expanded inline. Independent of the
 * MessageBubble popup — operators sometimes want to keep one source
 * pinned while scanning the answer. */
const expandedN = ref<number | null>(null);

function toggleExpanded(n: number) {
  expandedN.value = expandedN.value === n ? null : n;
}

/** Group hits into one display row per `## ` SECTION of a source document —
 * the highest-scoring chunk wins as the visible chip. Section-expansion
 * (ADR 0018 Phase D, task #290) intentionally fans the LLM context out with
 * `score=0.0` sibling chunks, which would otherwise pollute the citation
 * list with N near-duplicate rows. The siblings still live in the underlying
 * `retrieval.hits` array so `[N]` anchors from the answer markdown still
 * resolve via the per-hit `id` namespace — the UI just doesn't render them
 * as separate rows.
 *
 * Grouping by section rather than by file matters on a corpus dominated by
 * ONE large document (a standard, a handbook). Keyed on `source` alone,
 * every chunk of that document collapsed into a single row — the reader got
 * one card reading "+117 sections" and no way to tell which part of the
 * document supported which claim. Keyed on the section, the same answer
 * lists the sections it actually drew on (Context of Use, Functional Risk
 * Assessment, Model Testing …) and the repeats fold into one row each. */
interface GroupedHit {
  primary: Hit;
  /** Hidden sibling chunks (same source, lower score). Used for the
   * "+N sections" badge; the operator can drill into them via the
   * expanded preview pane if they want details. */
  siblings: Hit[];
}

/** Human label for where a hit came from — SQL vs Risma contract docs vs
 * web vs uploads. Derived from fields every projection already sets
 * (`kind`, `scope_tags`, `url`).
 *
 * The scope-tag rules below are naming CONVENTIONS an operator opts into,
 * not facts about the source. A corpus tagged for its subject rather than
 * its medium — `rockwool`, `ai-governance`, anything a customer names
 * after their own domain — matched none of them and every hit fell through
 * to "Doc", including crawled web pages whose own link was right next to
 * the badge saying otherwise. So when no convention matches, read the type
 * off the URL, which cannot disagree with itself. */
function hitBadge(h: Hit): string {
  if (h.kind === "sql") return "SQL";
  if (h.kind === "profile") return "Profile";
  const tags = h.scope_tags ?? [];
  if (tags.includes("web")) return "Web";
  if (tags.includes("upload")) return "Upload";
  if (tags.some((t) => t === "kb" || t.startsWith("kb:"))) return "KB";
  if (tags.includes("risma") || tags.includes("contracts")) return "Risma";
  if (tags.includes("offers")) return "Tender lib";
  // DOIT's own offerings corpus (danofficeit.com crawl + catalogs).
  if (tags.includes("offerings")) return "Offerings";
  const ref = h.url || h.source || "";
  // Strip any query/fragment before testing the extension: a PDF served
  // as `…/report.pdf?version=2` is still a PDF.
  const path = ref.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith(".pdf")) return "PDF";
  if (/^https?:\/\//.test(ref)) return "Web";
  return "Doc";
}

const groupedHits = computed<GroupedHit[]>(() => {
  // Stable order: keep groups in the order their primary hit appears in
  // the raw `hits` array (which is already sorted by score / position).
  const seen = new Map<string, GroupedHit>();
  const ordered: GroupedHit[] = [];
  for (const h of props.retrieval.hits) {
    // run_sql pseudo-hits are one row PER QUERY (keyed by id) — grouping
    // them by source once collapsed 8 queries into one entry fronted by
    // an empty result ("0 rækker +7 sections"), hiding the evidence.
    // \u0000 separates the two parts so a source ending in the section's
    // own text can't collide with a different source/section pair.
    const parent = h.source || h.url || h.id; // fallback so empty source never collapses unrelated hits
    const key =
      h.kind === "sql" ? h.id : `${parent}\u0000${h.section_title ?? ""}`;
    const existing = seen.get(key);
    if (!existing) {
      const group: GroupedHit = { primary: h, siblings: [] };
      seen.set(key, group);
      ordered.push(group);
      continue;
    }
    // New hit for an existing source. Promote it to primary if it
    // outscores the current primary — preserves "best per source"
    // semantics regardless of input order.
    if (h.score > existing.primary.score) {
      existing.siblings.push(existing.primary);
      existing.primary = h;
    } else {
      existing.siblings.push(h);
    }
  }
  return ordered;
});
</script>

<template>
  <aside class="retrieval" aria-label="retrieval sources">
    <details class="retrieval__shell">
      <summary class="retrieval__header">
        <span class="retrieval__label">
          sources
          <span v-if="groupedHits.length" class="retrieval__count">
            ({{ groupedHits.length
            }}<template v-if="retrieval.hits.length > groupedHits.length">
              · +{{
                retrieval.hits.length - groupedHits.length
              }}
              expanded</template
            >)
          </span>
        </span>
        <span class="retrieval__groups">
          <GroupPill v-for="g in displayGroups" :key="g" :group="g" />
        </span>
      </summary>
      <div
        v-if="retrieval.rewritten_query"
        class="retrieval__rewrite"
        title="Your follow-up was rewritten into a standalone retrieval query"
      >
        <span class="retrieval__rewrite-label">rewritten</span>
        <code>{{ retrieval.rewritten_query }}</code>
      </div>
      <details class="retrieval__filter">
        <summary>filter used</summary>
        <code class="code-block">{{ retrieval.filter }}</code>
      </details>
      <ol v-if="groupedHits.length" class="retrieval__hits">
        <li
          v-for="g in groupedHits"
          :key="g.primary.id"
          class="retrieval__hit"
          :id="turnId ? `source-${turnId}-${g.primary.n}` : undefined"
        >
          <!-- Hidden scroll anchors for the section-expanded siblings so
             [N] clicks from the answer markdown still land on this row
             even when N belongs to a sibling chunk we don't render. -->
          <span
            v-for="s in g.siblings"
            :key="s.id"
            :id="turnId ? `source-${turnId}-${s.n}` : undefined"
            class="retrieval__sibling-anchor"
            aria-hidden="true"
          ></span>
          <button
            type="button"
            class="retrieval__hit-row"
            :aria-expanded="expandedN === g.primary.n"
            @click="toggleExpanded(g.primary.n)"
          >
            <span class="retrieval__n">[{{ g.primary.n }}]</span>
            <span
              class="retrieval__kind"
              :class="`retrieval__kind--${hitBadge(g.primary).toLowerCase().replace(' ', '-')}`"
              >{{ hitBadge(g.primary) }}</span
            >
            <span class="retrieval__title" :title="g.primary.title">{{
              g.primary.title
            }}</span>
            <span
              v-if="g.primary.section_title"
              class="retrieval__section"
              :title="displaySectionTitle(g.primary.section_title)"
            >
              · {{ displaySectionTitle(g.primary.section_title) }}
            </span>
            <!-- SQL results and whole uploads have no relevance score — 1.000 is noise.
               The span stays (empty) because margin-left:auto on it
               right-aligns the rest of the row's meta. -->
            <span class="retrieval__score">{{
              g.primary.kind === "sql" || g.primary.kind === "upload"
                ? ""
                : g.primary.score.toFixed(3)
            }}</span>
            <span
              v-if="g.siblings.length"
              class="retrieval__expansion-badge"
              :title="`Section expansion pulled ${g.siblings.length} further chunk${g.siblings.length === 1 ? '' : 's'} of this section into the LLM's context`"
            >
              +{{ g.siblings.length }} chunk{{
                g.siblings.length === 1 ? "" : "s"
              }}
            </span>
            <span class="retrieval__acls">
              <GroupPill v-for="a in g.primary.acl" :key="a" :group="a" />
            </span>
            <span class="retrieval__chevron" aria-hidden="true">
              {{ expandedN === g.primary.n ? "▾" : "▸" }}
            </span>
          </button>
          <div v-if="expandedN === g.primary.n" class="retrieval__detail">
            <p v-if="g.primary.chunk_summary" class="retrieval__summary">
              {{ g.primary.chunk_summary }}
            </p>
            <blockquote v-if="g.primary.snippet" class="retrieval__snippet">
              {{ g.primary.snippet
              }}<span v-if="g.primary.snippet.length >= 200">…</span>
            </blockquote>
            <p
              v-if="g.primary.cites && g.primary.cites.length"
              class="retrieval__cites"
            >
              <span class="retrieval__cites-label">related</span>
              <code
                v-for="c in g.primary.cites"
                :key="c"
                class="retrieval__cites-token"
                >{{ c }}</code
              >
            </p>
            <details v-if="g.siblings.length" class="retrieval__siblings">
              <summary>
                {{ g.siblings.length }} more section{{
                  g.siblings.length === 1 ? "" : "s"
                }}
                from this file in the LLM's context
              </summary>
              <ul class="retrieval__siblings-list">
                <li v-for="s in g.siblings" :key="s.id">
                  <span class="retrieval__siblings-section">
                    {{ displaySectionTitle(s.section_title) || g.primary.title }}
                  </span>
                </li>
              </ul>
            </details>
            <div class="retrieval__detail-meta">
              <span
                v-if="g.primary.topics && g.primary.topics.length"
                class="retrieval__topics"
              >
                <GroupPill v-for="t in g.primary.topics" :key="t" :group="t" />
              </span>
              <a
                v-if="g.primary.url"
                :href="g.primary.url"
                target="_blank"
                rel="noreferrer"
                class="retrieval__link"
                >open source ↗</a
              >
              <span v-else class="retrieval__path">{{ g.primary.source }}</span>
            </div>
          </div>
        </li>
      </ol>
      <p v-else class="retrieval__empty">
        (no sources match this user's ACL — the answer will fall back to "I
        don't have information about that")
      </p>
    </details>
  </aside>
</template>

<style scoped>
.retrieval {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  font-size: 0.85rem;
}
.retrieval__shell > summary {
  /* The whole disclosure-summary row should look like a clickable header
   * with the default <details> marker on the right. */
  cursor: pointer;
  list-style: revert;
  user-select: none;
}
.retrieval__shell[open] > summary {
  margin-bottom: var(--space-2);
}
.retrieval__count {
  color: var(--color-muted);
  font-weight: normal;
  margin-left: var(--space-1);
}
.retrieval__hit {
  /* Anchor children below scope their absolute positioning to this <li>. */
  position: relative;
}
.retrieval__sibling-anchor {
  /* Zero-size scroll target for `[N]` clicks where N belongs to a
   * section-expanded sibling we don't render as a separate row. The
   * anchor sits inside the primary's <li>, so scroll-into-view lands
   * the operator on the source group as intended.
   *
   * MUST be out of normal flow: a primary with 19 expansion siblings
   * would otherwise stack 19 block-level spans inside its <li> and
   * the line-height of each (even with height:0) accumulates into a
   * visible vertical gap. `position: absolute` removes them from
   * flow entirely while keeping a real DOM position the browser
   * can scroll to. */
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
.retrieval__expansion-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 8%, transparent);
  color: var(--color-muted);
  margin-left: var(--space-1);
  white-space: nowrap;
  flex-shrink: 0;
}
.retrieval__siblings {
  margin-top: var(--space-2);
  font-size: 0.8rem;
}
.retrieval__siblings > summary {
  cursor: pointer;
  color: var(--color-muted);
  user-select: none;
}
.retrieval__siblings-list {
  list-style: disc inside;
  margin: var(--space-1) 0 0 var(--space-2);
  padding: 0;
  color: var(--color-muted);
}
.retrieval__siblings-section {
  font-style: italic;
}
.retrieval__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px dashed var(--color-border);
  margin-bottom: var(--space-2);
}
.retrieval__label {
  font-weight: 500;
  color: var(--color-ink);
}
.retrieval__groups {
  display: inline-flex;
  gap: 0.25rem;
}
.retrieval__rewrite {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  font-size: 0.8rem;
  padding: 0.3rem var(--space-2);
  margin-bottom: var(--space-2);
  background: var(--color-surface-alt);
  border-radius: var(--radius-sm);
  border-left: 2px solid var(--color-accent);
}
.retrieval__rewrite-label {
  color: var(--color-accent);
  font-weight: 500;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.retrieval__rewrite code {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--color-ink);
}
.retrieval__filter {
  margin-bottom: var(--space-2);
}
.retrieval__filter summary {
  color: var(--color-muted);
  cursor: pointer;
  font-size: 0.8rem;
}
.retrieval__hits {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.retrieval__hit {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  border-radius: var(--radius-sm);
  scroll-margin-top: var(--space-4);
}
.retrieval__hit--flash {
  animation: cite-flash 1.4s ease-out;
}
@keyframes cite-flash {
  0% {
    background: color-mix(in srgb, var(--color-accent) 30%, transparent);
  }
  100% {
    background: transparent;
  }
}
.retrieval__hit-row {
  display: flex;
  /* Single line — title / section truncate with ellipsis rather than
   * wrap. Wrapping every-overflowing-bit looks especially bad for
   * Risma-style filenames that repeat the title in the URL stem, and
   * pushes score / badges / chevron to a second row. */
  flex-wrap: nowrap;
  align-items: baseline;
  gap: var(--space-2);
  background: none;
  border: none;
  padding: 0.25rem 0.4rem;
  margin: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: var(--radius-sm);
  color: inherit;
  /* Prevent the row itself from overflowing its <li>. */
  min-width: 0;
}
.retrieval__hit-row:hover {
  background: var(--color-surface-alt);
}
.retrieval__n {
  font-family: var(--font-mono);
  color: var(--color-accent);
  font-weight: 500;
  flex-shrink: 0;
}
.retrieval__kind {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  border: 1px solid transparent;
  /* Neutral default (Doc / KB / Upload / Tender lib) */
  color: var(--color-muted);
  background: color-mix(in srgb, var(--color-ink) 8%, transparent);
}
/* Danoffice brand accents per origin: teal = structured SQL, navy =
 * Risma contract docs, coral = public web. rgba tints keep them
 * readable on both light and dark surfaces. */
.retrieval__kind--sql {
  color: #279db6;
  background: rgba(39, 157, 182, 0.14);
  border-color: rgba(39, 157, 182, 0.35);
}
.retrieval__kind--risma {
  color: var(--color-ink);
  background: rgba(27, 48, 73, 0.12);
  border-color: rgba(27, 48, 73, 0.3);
}
.retrieval__kind--web {
  color: #db7363;
  background: rgba(219, 115, 99, 0.14);
  border-color: rgba(219, 115, 99, 0.35);
}
.retrieval__kind--doit {
  color: #5a7a4a;
  background: rgba(193, 214, 182, 0.25);
  border-color: rgba(193, 214, 182, 0.6);
}
.retrieval__title {
  font-weight: 500;
  color: var(--color-ink);
  /* Shrink-first flex item: take whatever room is left after the
   * fixed right-side meta (score, badge, chevron). Ellipsis kicks in
   * once the filename exceeds that budget. `min-width: 0` is the
   * standard "let me shrink below my content's intrinsic size" hack
   * required when a flex child has overflow: hidden. Full title is
   * available on hover via the row's title attribute. */
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.retrieval__section {
  color: var(--color-muted);
  font-size: 0.85em;
  /* Section title also shrinks/ellipsizes; the row stays single-line. */
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.retrieval__score {
  color: var(--color-muted);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  margin-left: auto;
  /* Right-side meta items don't shrink — title ellipsizes instead. */
  flex-shrink: 0;
}
.retrieval__acls {
  display: inline-flex;
  gap: 0.25rem;
  flex-shrink: 0;
}
.retrieval__chevron {
  color: var(--color-muted);
  font-size: 0.8em;
  width: 0.9em;
  text-align: center;
  flex-shrink: 0;
}
.retrieval__detail {
  padding: 0.5rem 0.75rem;
  margin-left: 1.25rem;
  border-left: 2px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: 0.85em;
}
.retrieval__summary {
  margin: 0;
  color: var(--color-ink);
}
.retrieval__snippet {
  margin: 0;
  padding: 0.4rem 0.75rem;
  background: var(--color-surface-alt);
  border-left: 3px solid var(--color-accent);
  border-radius: var(--radius-sm);
  font-size: 0.85em;
  color: var(--color-muted);
  white-space: pre-wrap;
}
.retrieval__detail-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}
.retrieval__topics {
  display: inline-flex;
  gap: 0.25rem;
}
.retrieval__cites {
  margin: 0 0 var(--space-2) 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  font-size: 0.75rem;
}
.retrieval__cites-label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  color: var(--color-muted);
}
.retrieval__cites-token {
  font-size: 0.8em;
  background: var(--color-surface-alt);
  padding: 0.05em 0.4em;
  border-radius: var(--radius-sm);
  color: var(--color-muted);
}
.retrieval__link {
  margin-left: auto;
  color: var(--color-accent);
  text-decoration: none;
}
.retrieval__link:hover {
  text-decoration: underline;
}
.retrieval__path {
  margin-left: auto;
  font-family: var(--font-mono, monospace);
  font-size: 0.75em;
  color: var(--color-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 24rem;
}
.retrieval__empty {
  color: var(--color-error);
  font-style: italic;
  margin: 0;
}
</style>
